export const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const getSpeakerMeta = (speaker, fallbackIndex = 0) => {
  const normalized = (speaker || "").toString().trim().toLowerCase();
  if (normalized.includes("support") || normalized.includes("agent") || normalized.includes("konuşmacı 1") || normalized.includes("konusmaci 1") || normalized === "1") {
    return { label: "Agent", badge: "1", tone: "agent", isAgent: true };
  }
  if (normalized.includes("client") || normalized.includes("customer") || normalized.includes("müşteri") || normalized.includes("musteri") || normalized.includes("konuşmacı 2") || normalized.includes("konusmaci 2") || normalized === "2") {
    return { label: "Müşteri", badge: "2", tone: "customer", isAgent: false };
  }
  const num = fallbackIndex + 1;
  return {
    label: speaker || `Konuşmacı ${num}`,
    badge: String(num),
    tone: num === 1 ? "agent" : "customer",
    isAgent: num === 1,
  };
};

export const parseTimestampToSeconds = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const str = String(value).trim();
  if (!str) return null;
  if (/^\d+(\.\d+)?$/.test(str)) return parseFloat(str);
  const parts = str.split(":").map((p) => parseFloat(p));
  if (parts.some((p) => Number.isNaN(p))) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
};

export const enrichSegmentsWithTiming = (segments, totalDurationSec = 0) => {
  const enriched = segments.map((seg, idx) => {
    const startSeconds = parseTimestampToSeconds(seg.startTime);
    let endSeconds = parseTimestampToSeconds(seg.endTime);
    return { ...seg, id: seg.id ?? idx, startSeconds, endSeconds };
  });

  for (let i = 0; i < enriched.length; i += 1) {
    if (enriched[i].startSeconds != null && enriched[i].endSeconds == null) {
      const nextStart = enriched.slice(i + 1).find((s) => s.startSeconds != null)?.startSeconds;
      if (nextStart != null) enriched[i].endSeconds = nextStart;
    }
  }

  const hasTiming = enriched.some((s) => s.startSeconds != null);
  if (hasTiming || !totalDurationSec || enriched.length === 0) {
    return enriched;
  }

  const totalChars = enriched.reduce((sum, s) => sum + (s.text?.length || 0), 0) || 1;
  let cursor = 0;
  return enriched.map((seg) => {
    const portion = (seg.text?.length || 0) / totalChars;
    const startSeconds = cursor;
    const endSeconds = cursor + portion * totalDurationSec;
    cursor = endSeconds;
    return { ...seg, startSeconds, endSeconds, estimatedTiming: true };
  });
};

export const findActiveSegmentIndex = (segments, currentTimeSec) => {
  if (!segments.length || currentTimeSec == null || Number.isNaN(currentTimeSec)) return -1;

  // Sessizlik / bekleme anında son başlamış segmentte kal (başa atlama)
  let lastStartedIndex = -1;
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    if (seg.startSeconds != null && currentTimeSec >= seg.startSeconds - 0.05) {
      lastStartedIndex = i;
    }
  }

  return lastStartedIndex;
};

export const formatSecondsAsTimestamp = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return "";
  const s = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export const parseTranscriptSegments = (record) => {
  if (!record) return [];
  let raw = record.transcript_data;
  if (typeof raw === "string") {
    try { raw = JSON.parse(raw); } catch { raw = null; }
  }
  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .filter((seg) => seg && (seg.text || seg.transcript))
      .map((seg, idx) => ({
        speaker: seg.speaker || `Konuşmacı ${idx + 1}`,
        text: (seg.text || seg.transcript || "").trim(),
        startTime: seg.startTime || seg.start_time || (seg.start != null ? formatSecondsAsTimestamp(seg.start) : ""),
        endTime: seg.endTime || seg.end_time || (seg.end != null ? formatSecondsAsTimestamp(seg.end) : ""),
      }));
  }
  const fallback = [];
  if (record.agent_text) fallback.push({ speaker: "Agent", text: record.agent_text, startTime: "", endTime: "" });
  if (record.customer_text) fallback.push({ speaker: "Müşteri", text: record.customer_text, startTime: "", endTime: "" });
  if (fallback.length) return fallback;
  if (record.transcript_text) {
    return [{ speaker: "Transkript", text: record.transcript_text, startTime: "00:00", endTime: "" }];
  }
  return [];
};

export const groupSegmentsBySpeaker = (segments) => {
  const groups = [];
  const map = new Map();
  segments.forEach((seg, idx) => {
    const meta = getSpeakerMeta(seg.speaker, idx);
    const key = meta.label;
    if (!map.has(key)) {
      const entry = { ...meta, texts: [] };
      map.set(key, entry);
      groups.push(entry);
    }
    map.get(key).texts.push(seg.text);
  });
  return groups.map((g) => ({ ...g, preview: g.texts.join(" ").slice(0, 220) }));
};

export const getTranscriptSearchSnippet = (record, query) => {
  if (!record || !query?.trim()) return null;
  const q = query.trim().toLowerCase();
  const sources = [record.summary_text, record.transcript_text, record.agent_text, record.customer_text].filter(Boolean);
  for (const src of sources) {
    const idx = src.toLowerCase().indexOf(q);
    if (idx >= 0) {
      const start = Math.max(0, idx - 35);
      const end = Math.min(src.length, idx + q.length + 35);
      return `${start > 0 ? "..." : ""}${src.slice(start, end)}${end < src.length ? "..." : ""}`;
    }
  }
  return null;
};

export const speakerBubbleClass = (tone, isDark) => {
  if (tone === "agent") {
    return isDark
      ? "bg-blue-500/15 border-blue-500/25 text-slate-100"
      : "bg-blue-50 border-blue-100 text-gray-900";
  }
  if (tone === "customer") {
    return isDark
      ? "bg-violet-500/15 border-violet-500/25 text-slate-100"
      : "bg-violet-50 border-violet-100 text-gray-900";
  }
  return isDark
    ? "bg-slate-800/80 border-slate-700 text-slate-200"
    : "bg-gray-50 border-gray-200 text-gray-800";
};

export const speakerBadgeClass = (tone, isDark) => {
  if (tone === "agent") return "bg-blue-500 text-white";
  if (tone === "customer") return "bg-violet-500 text-white";
  return isDark ? "bg-slate-600 text-white" : "bg-gray-500 text-white";
};

export const speakerNameClass = (tone, isDark) => {
  if (tone === "agent") return isDark ? "text-blue-300" : "text-blue-700";
  if (tone === "customer") return isDark ? "text-violet-300" : "text-violet-700";
  return isDark ? "text-slate-300" : "text-gray-700";
};

export const normalizeSummaryText = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/\r\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\t/g, " ")
    .trim();
};

export const parseSummaryBlocks = (text) => {
  const normalized = normalizeSummaryText(text);
  if (!normalized) return [];

  const lines = normalized.split("\n");
  const blocks = [];
  let paragraphLines = [];
  let listBlock = null;

  const flushParagraph = () => {
    const merged = paragraphLines.join(" ").replace(/\s+/g, " ").trim();
    if (merged) blocks.push({ type: "paragraph", text: merged });
    paragraphLines = [];
  };

  const flushList = () => {
    if (listBlock?.items?.length) blocks.push(listBlock);
    listBlock = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      flushParagraph();
      continue;
    }

    const bulletMatch = line.match(/^[-*•–—]\s+(.+)/);
    const numberedMatch = line.match(/^\d+[.)]\s+(.+)/);
    const headingMatch = line.match(/^(#{1,3}\s+)?(.{2,80}):$/);

    if (bulletMatch) {
      flushParagraph();
      if (!listBlock || listBlock.type !== "bullet") {
        flushList();
        listBlock = { type: "bullet", items: [] };
      }
      listBlock.items.push(bulletMatch[1].trim());
      continue;
    }

    if (numberedMatch) {
      flushParagraph();
      if (!listBlock || listBlock.type !== "numbered") {
        flushList();
        listBlock = { type: "numbered", items: [] };
      }
      listBlock.items.push(numberedMatch[1].trim());
      continue;
    }

    if (headingMatch && !line.includes(". ")) {
      flushList();
      flushParagraph();
      blocks.push({ type: "heading", text: headingMatch[2].trim() });
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushList();
  flushParagraph();

  if (blocks.length === 0 && normalized) {
    const sentences = normalized.split(/(?<=[.!?])\s+/).filter(Boolean);
    if (sentences.length > 1) {
      return sentences.map((s) => ({ type: "paragraph", text: s.trim() }));
    }
    return [{ type: "paragraph", text: normalized }];
  }

  return blocks;
};
